// State selectors for the product config flow: what the product being
// configured (productConfig.currentProduct) still needs from the customer,
// and the derived UI/completion state built on top. They compose the pure
// product predicates (productConfigPredicates) with the shoot state the
// predicates can't know about.
import { createSelector } from 'reselect';
import { personalizationDeferredToCheckout } from '../utils/personalizationOffer';
import {
  productNeedsBackgroundSelection,
  productOffersCropOptions,
} from '../components/v3/products/productConfigPredicates';
import {
  backgroundSlotsCount,
  getImageRecords,
  ImageRecord,
  individualRecordsCount,
  posesCountFromRecords,
  productPosesCount,
} from '../components/v3/products/imageRecords';

// Whether the product being configured needs a pose picked. Pose slots come
// from the universal image records list (one record per pose) — so
// multiple-image products expose one slot per node even when legacy
// poses_count is 0. Prepay orders never offer pose selection (the photos are
// not taken yet), so this is a state selector: the answer depends on the
// shoot's prepay mode as much as on the product.
export const poseConfigurationRequiredSelector = (state: any): boolean =>
  !state.shoot?.isPrepay &&
  productPosesCount(state.productConfig?.currentProduct) > 0;

// Whether the product being configured has any pose/background slots for the
// customer to fill. Runtime availability beyond prepay (gallery images, shoot
// background options) is layered on top by the callers that have that context.
export const productRequiresConfigurationSelector = (state: any): boolean =>
  poseConfigurationRequiredSelector(state) ||
  productNeedsBackgroundSelection(state.productConfig?.currentProduct);

// Whether a background step is actually available for the product being
// configured: the product must need a background picked
// (productNeedsBackgroundSelection — background slots of its own, not
// auto-resolved) AND the shoot must offer backgrounds to pick from. A product
// with background slots on a shoot without a catalogue has nothing to select.
export const backgroundConfigurationRequiredSelector = (state: any): boolean =>
  productNeedsBackgroundSelection(state.productConfig?.currentProduct) &&
  (state.shoot?.backgroundOptions?.length ?? 0) > 0;

// Whether each pose slot has any source images to pick from — one boolean per
// slot, in slot order.
//
// Availability is a PER-SLOT question, resolved the same way
// selectTransformedPoses resolves the picker's pose source: a group slot draws
// from studentGroupImages and deliberately never falls back to the individual
// gallery. So a gallery holding a class photo but no individual poses has
// something to pick for its group slot and nothing for its individual ones.
//
// This is the single source of truth for that rule. It drives WHERE the picker
// goes, not WHETHER the product is complete: the slot navigator
// (pickNextConfigSlot) and the desktop auto-open (autoOpenConfigPanel, via the
// navigator) consume it, so neither lands on a slot with nothing to show.
//
// The Add-to-Cart gate deliberately does NOT consult it. Every pose slot must be
// filled regardless of what the gallery can supply, matching the background
// branch — an unfillable slot blocks rather than being waived, because waiving it
// shipped an order missing a required pose. Both call sites once carried
// diverging gallery-wide copies of this rule, which is how an untouched group
// product both completed and refused to auto-open.
export const selectPoseSlotsFillable = createSelector(
  (state: any) => state.productConfig?.currentProduct,
  (state: any) => state.additionalData,
  (product, additionalData): boolean[] => {
    const {
      availablePoses,
      studentGallery,
      allAvailablePosesForOrder,
      studentGroupImages,
    } = additionalData ?? {};

    const individualHasImages =
      (availablePoses?.length ?? 0) > 0 ||
      (studentGallery?.length ?? 0) > 0 ||
      (allAvailablePosesForOrder?.length ?? 0) > 0;
    const groupHasImages = (studentGroupImages?.length ?? 0) > 0;

    return getImageRecords(product).map((record) =>
      record.type === 'group' ? groupHasImages : individualHasImages,
    );
  },
);

// The first pose slot the customer could actually pick for, or null when none
// can be filled. Used to seed a picker on a slot that has something to show.
export const selectFirstFillablePoseSlot = createSelector(
  selectPoseSlotsFillable,
  (fillable): number | null => {
    const index = fillable.findIndex(Boolean);
    return index === -1 ? null : index;
  },
);

// The product needs a background the shoot cannot offer, so its configuration
// can never be completed: the background step is unavailable (no catalogue to
// pick from, per backgroundConfigurationRequiredSelector) while
// selectInitialStepsComplete still requires a background — deliberately, so a
// missing catalogue can never make an order look complete. Add to Cart therefore
// stays blocked with nothing the customer can do about it.
//
// That combination is a studio misconfiguration (a green-screen shoot with no
// background catalogue), not a customer-facing state, so the UI says so and
// reports it. Scoped to green-screen shoots because that is the only place the
// gate reaches its background branch: a non-green-screen shoot lets poses alone
// gate completion, and a product with no pose slots completes outright.
export const selectBackgroundCatalogueMissing = createSelector(
  (state: any) => state.productConfig?.currentProduct,
  (state: any) => state.shoot,
  (product, shoot): boolean =>
    Boolean(shoot?.isGreenScreen) &&
    productNeedsBackgroundSelection(product) &&
    (shoot?.backgroundOptions?.length ?? 0) === 0 &&
    productPosesCount(product) > 0,
);

// Crop options apply to individual poses only — the rule itself lives in
// productOffersCropOptions, shared with both wizards. This binds it to the
// product being configured. The single-product screen adds no prepay condition:
// unlike the wizards, it offers crops wherever the shoot does.
export const selectShowCropOptions = (state: any): boolean =>
  productOffersCropOptions(state.productConfig.currentProduct, state.shoot);

// The same rule for the multi-config bundle, which asks it of each CHILD of the
// product it is configuring rather than of one product in the store. The child
// comes from a prop, so there is nothing to key a plain selector on — this
// returns the predicate instead, memoized on `state.shoot` so the function
// identity is stable between renders.
//
// The bundle adds prepay on top: it only offers crops on a prepay shoot, while
// the single-product screen (selectShowCropOptions above) offers them wherever
// the shoot does.
export const selectCropOptionsPredicate = createSelector(
  (state: any) => state.shoot,
  (shoot) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (product: any): boolean =>
      Boolean(shoot?.isPrepay) && productOffersCropOptions(product, shoot),
);

export const selectInitialStepsComplete = createSelector(
  selectShowCropOptions,
  (state: any) => state.productConfig.currentProduct,
  (state: any) => state.productConfig.cropSelections,
  (state: any) => state.productConfig.poseSelections,
  (state: any) => state.productConfig.backgroundSelections,
  (state: any) => state.shoot,
  (state: any) => state.additionalData,
  (
    showCropOptions,
    product,
    cropSelections,
    poseSelections,
    backgroundSelections,
    shoot,
    additionalData,
  ) => {
    if (!product) return false;

    const records = getImageRecords(product);
    const posesCount: number = posesCountFromRecords(records);
    const individualCount: number = individualRecordsCount(records);
    const productBackgroundSlotsCount: number = backgroundSlotsCount(product);
    const needsBackgroundSelection: boolean =
      productNeedsBackgroundSelection(product);

    const { isPrepay, isGreenScreen, backgroundOptions } = shoot;
    const { isGeskus, picsGalleryId, studentPreviewImageUrl } = additionalData;

    // Geskus autoselect: the parent entered a student key that resolved through
    // Datum#student_shoot_key, so KeyLookup::GeskusLookupService returned no
    // gallery and no images (`pics_gallery_id: nil`, `image_urls: []`) and
    // ShootProductsController presigned the student's single assigned photo into
    // studentPreviewImageUrl instead. There is no pose grid and never will be,
    // so poseSelections stays empty for the entire session BY DESIGN — the photo
    // is already chosen, just not by the customer.
    //
    // Same pair of fields PoseSlot uses to paint that photo into the slot. A
    // Geskus GALLERY order has picsGalleryId set and real availablePoses, so it
    // keeps the every-slot rule.
    const posesAutoAssigned: boolean = Boolean(
      isGeskus && !picsGalleryId && studentPreviewImageUrl,
    );

    // PosesConfig stores each selection at its own slot index, so filling a
    // later slot first leaves a HOLE at the earlier one:
    // `[<1 empty>, pose]`.length is already 2. Only filter(Boolean) counts how
    // many slots are actually filled — `.length` reports a half-configured
    // product as complete (ENG-2630).
    const posesFilled: number = poseSelections.filter(Boolean).length;

    // EVERY pose slot must be filled — including a slot whose own source has no
    // images. A slot the customer cannot fill used to count as satisfied, on the
    // grounds that gating on it would dead-end Add to Cart; but letting it
    // through submits an order item missing a photo the product requires, which
    // is worse. It is the same call the background branch below already made
    // (see selectBackgroundCatalogueMissing): an unsatisfiable requirement blocks
    // rather than resolving itself. The customer gets AddToCartButton's "Please
    // finish configuring your product" on click, and the picker explains which
    // photo is missing via NoPoseImageMessage.
    //
    // Two exemptions, and only two, both cases where the flow never offers pose
    // selection at all: prepay (the photos aren't taken yet) and Geskus
    // autoselect (the photo is assigned server-side — see posesAutoAssigned).
    // Everywhere a picker exists, every slot must be filled.
    const posesSatisfied: boolean =
      isPrepay ||
      posesAutoAssigned ||
      records.every((_record: ImageRecord, index: number) =>
        Boolean(poseSelections[index]),
      );

    if (
      showCropOptions &&
      cropSelections.filter(Boolean).length < individualCount
    ) {
      return false;
    }

    if (isPrepay && !needsBackgroundSelection) {
      return true;
    }

    if (isPrepay && !isGreenScreen) {
      return true;
    }

    // No background step (no slots of its own, or a skip-background digital
    // download): poses alone gate completeness.
    if (!needsBackgroundSelection) {
      return posesSatisfied;
    }

    // Geskus keeps its background leniency — outdoor shoots need none at all, and
    // green-screen ones are satisfied by the backgrounds alone — but NOT pose
    // leniency. Both branches used to `return true` outright, completing a
    // multi-slot product with some or none of its poses picked. Nothing threw:
    // the cart simply received fewer pose ids than the product has slots, so a
    // two-pose product shipped with one image (ENG-2630).
    //
    // `posesSatisfied` is the same rule every other studio gets, so a Geskus
    // GALLERY product with an empty slot now blocks like anyone else's. The
    // autoselect path is not "an unfillable slot" — it is a slot already filled
    // server-side — and posesSatisfied exempts it above. Reading these branches
    // as blanket pose leniency was the mistake that broke prod key V9NKQQQ9: the
    // bypass was load-bearing for the Datum path, not a corner case. See GA3 in
    // docs/qa-plan-config-panel-refactor.md.
    if (isGeskus && !isPrepay && !isGreenScreen && posesCount > 0) {
      return posesSatisfied;
    }

    if (
      isGeskus &&
      !isPrepay &&
      isGreenScreen &&
      productBackgroundSlotsCount ===
        backgroundSelections.filter(Boolean).length
    ) {
      return posesSatisfied;
    }

    if (backgroundOptions.length === 0 && posesCount === 0) {
      return true;
    }

    if (posesCount > 0 && !posesSatisfied && !isPrepay) {
      return false;
    }

    // Non-green-screen shoots never offer a background catalogue, so a product
    // carrying background slots can never have them filled here — poses are the
    // only thing left to gate on, and the background branch below would refuse
    // forever. (posesSatisfied is necessarily true by now: the guard above
    // returned on the false case. It is spelled out rather than `true` so the
    // rule reads locally.)
    if (!isGreenScreen && !isPrepay && !isGeskus && posesCount > 0) {
      return posesSatisfied;
    }

    // Backgrounds never stand in for an unfilled pose slot — a product with
    // background slots still needs every pose. This gate used to check the
    // background count alone, which let a green-screen multi-node product
    // (e.g. a Memory Mate with its group slot filled and its one background
    // picked) reach Add to Cart with the portrait slot empty.
    if (productBackgroundSlotsCount > 0) {
      return (
        posesSatisfied &&
        productBackgroundSlotsCount ===
          backgroundSelections.filter(Boolean).length
      );
    }

    return true;
  },
);

// The config-time personalization offer is derived from shoot state — never
// stored. It is suppressed only for prepay sessions of studios that defer the
// prompt to checkout; the rule itself, and why postpay must always be offered
// here, live in utils/personalizationOffer.
export const selectShouldOfferPersonalization = (state: any): boolean =>
  !personalizationDeferredToCheckout(state.shoot);

export const selectCanBeSubmitted = createSelector(
  selectInitialStepsComplete,
  selectShouldOfferPersonalization,
  (state: any) => state.productConfig.personalizationOffered,
  (state: any) => state.productConfig.currentProduct,
  (
    initialStepsComplete,
    shouldOfferPersonalization,
    personalizationOffered,
    product,
  ) => {
    const personalizationRequired =
      product?.personalization_status === 'personalization_required';
    const personalizationOptional =
      product?.personalization_status === 'personalization_optional';
    const personalizationComplete =
      !shouldOfferPersonalization ||
      personalizationOffered ||
      !(personalizationOptional || personalizationRequired);
    return initialStepsComplete && personalizationComplete;
  },
);

export const selectShowPersonalizationForm = createSelector(
  selectInitialStepsComplete,
  selectShouldOfferPersonalization,
  (state: any) => state.productConfig.personalizationOffered,
  (state: any) => state.productConfig.currentProduct,
  (
    initialStepsComplete,
    shouldOfferPersonalization,
    personalizationOffered,
    product,
  ) => {
    const personalizationRequired =
      product?.personalization_status === 'personalization_required';
    const personalizationOptional =
      product?.personalization_status === 'personalization_optional';
    return (
      initialStepsComplete &&
      shouldOfferPersonalization &&
      !personalizationOffered &&
      (personalizationOptional || personalizationRequired)
    );
  },
);

export const selectShowSMSNumberConfirmation = createSelector(
  selectCanBeSubmitted,
  (state: any) => state.productConfig.currentProduct,
  (canBeSubmitted, product) => canBeSubmitted && !!product?.sms_required,
);

export const selectShowDoneMessage = createSelector(
  selectCanBeSubmitted,
  (state: any) => state.productConfig.backgroundsPosesSelectorVisible,
  (state: any) => state.productConfig.currentProduct,
  (canBeSubmitted, backgroundsPosesSelectorVisible, product) =>
    canBeSubmitted &&
    !backgroundsPosesSelectorVisible &&
    !product?.sms_required,
);

export const selectTransformedPoses = createSelector(
  (state: any) => state.productConfig.currentProduct,
  (state: any) => state.productConfig.activeConfigSlotIndex,
  (state: any) => state.additionalData,
  (product, activeConfigSlotIndex, additionalData) => {
    const {
      allAvailablePosesForOrder: rawAllAvailablePosesForOrder = [],
      availablePoses: rawAvailablePoses = [],
      studentGallery: rawStudentGallery = [],
      studentGroupImages,
    } = additionalData || {};

    if (!product) {
      return {
        allAvailablePosesForOrder: rawAllAvailablePosesForOrder,
        availablePoses: rawAvailablePoses,
        studentGallery: rawStudentGallery,
      };
    }

    // The slot's record type decides its pose source. A group slot (a pure
    // group product, the trailing slot of a group+individual, or a group node
    // of a multiple-image product) shows the student's group images; every
    // other slot shows individual poses. Group slots deliberately get NO
    // fallback to the individual gallery when the gallery has no group image —
    // the pose picker shows a "no group image available" message instead.
    const records = getImageRecords(product);
    const slotIsGroup =
      activeConfigSlotIndex != null &&
      records[activeConfigSlotIndex]?.type === 'group';

    const allAvailablePosesForOrder = slotIsGroup
      ? []
      : rawAllAvailablePosesForOrder;
    const availablePoses = slotIsGroup
      ? studentGroupImages ?? []
      : rawAvailablePoses;
    const studentGallery = slotIsGroup
      ? studentGroupImages ?? []
      : rawStudentGallery;

    return { allAvailablePosesForOrder, availablePoses, studentGallery };
  },
);
